import { JSXElementConstructor, PropsWithChildren, ReactElement, useMemo } from 'react'

import { Box, darken, Stack, useTheme } from '@mui/material'
import { DataCard, type DataCardProps } from '@/components/card/DataCard/DataCard'

type CardTabProps = PropsWithChildren & {
  value: string
}

export const CardTab = ({ children }: CardTabProps) => <>{children}</>

export type Tab =
  | string
  | {
      value: string
      label?: string
    }

export type DataCardWithTabsProps = Omit<DataCardProps, 'children' | 'headerRow'> & {
  tabs: Tab[]
  onTabChange: (tabValue: string) => void
  currentTab: string
  children: ReactElement<CardTabProps, JSXElementConstructor<CardTabProps>>[]
}

const getTabValue = (tab: Tab) => {
  if (typeof tab === 'string') return tab
  if (typeof tab === 'object' && 'value' in tab) return tab.value
  return ''
}

export const DataCardWithTabs = (props: DataCardWithTabsProps) => {
  const { currentTab, onTabChange, tabs, children, ...rest } = props
  const theme = useTheme()

  const visibleTab = useMemo(
    () => children.find((child) => child.props.value === currentTab),
    [children, currentTab]
  )

  return (
    <DataCard {...rest}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          backgroundColor:
            theme.palette.mode === 'light' ? 'background.default' : 'background.paper',
          color: 'text.primary',
          ...rest.Sx?.header
        }}
      >
        {tabs.map((tab, idx) => {
          const tabValue = getTabValue(tab)
          const isActive = currentTab === tabValue
          const tabLabel = typeof tab === 'string' ? tab : tab?.label || tab.value

          return (
            <Box
              key={`tabs-${idx}`}
              sx={{
                py: 1,
                px: 2,
                pl: idx === 0 ? 2.2 : undefined,
                backgroundColor: ({ palette }) =>
                  isActive
                    ? palette.mode === 'light'
                      ? 'background.paper'
                      : 'grey.800'
                    : 'transparent',
                cursor: isActive ? 'default' : 'pointer',
                borderTopLeftRadius: ({ shape }) =>
                  idx === 0 ? 0 : Number(shape.borderRadius) * 2,
                borderTopRightRadius: ({ shape }) => Number(shape.borderRadius) * 2,
                '&:hover': {
                  backgroundColor: ({ palette }) =>
                    isActive
                      ? palette.mode === 'light'
                        ? 'background.paper'
                        : 'grey.800'
                      : darken(palette.background.paper, 0.075)
                }
              }}
              onClick={() => {
                if (!isActive) onTabChange(tabValue)
              }}
            >
              {tabLabel}
            </Box>
          )
        })}
      </Stack>

      {visibleTab}
    </DataCard>
  )
}
